import { io, Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents } from '@/types/chatbot';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';

/**
 * Socket.IO Service for real-time chatbot communication
 * Manages WebSocket connections with automatic reconnection and room management
 */
class SocketService {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private memberId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Connect to Socket.IO server and join member-specific room
   * @param memberId - Unique identifier for the team member
   * @returns Socket instance
   */
  connect(memberId: string): Socket<ServerToClientEvents, ClientToServerEvents> {
    // Reuse existing connection if already connected
    if (this.socket?.connected && this.memberId === memberId) {
      console.log('✅ Reusing existing socket connection');
      return this.socket;
    }

    // Disconnect previous connection if different member
    if (this.socket?.connected && this.memberId !== memberId) {
      console.log('🔄 Switching member connection');
      this.disconnect();
    }

    this.memberId = memberId;
    
    // Create new Socket.IO connection
    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,        // Connection timeout: 20 seconds
      autoConnect: true,
    });

    this.setupEventHandlers();

    return this.socket;
  }

  /**
   * Setup event handlers for Socket.IO connection
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection established
    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id);
      this.reconnectAttempts = 0;
      
      // Join member-specific room
      if (this.memberId && this.socket) {
        // Backend uses "member_" prefix for room names
        this.socket.emit('join_room', { memberId: this.memberId });
        console.log(`📡 Joining room for member: ${this.memberId}`);
        console.log(`📡 Expected backend room name: member_${this.memberId}`);
      }

      // 🔔 ALSO join notification room (NEW)
      if (this.memberId && this.socket) {
        this.socket.emit('join-user', this.memberId);
        console.log(`🔔 Joined notification room: user:${this.memberId}`);
      }

    });

    // Connection lost
    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      
      // Manual disconnect by client
      if (reason === 'io client disconnect') {
        console.log('🔌 Client initiated disconnect');
        return;
      }
      
      // Server disconnect - attempt reconnect
      if (reason === 'io server disconnect') {
        console.log('🔄 Server disconnected, attempting reconnect...');
        this.socket?.connect();
      }
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      console.error(`❌ Connection error (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}):`, error.message);
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Max reconnection attempts reached');
      }
    });

    // Server confirmation events
    this.socket.on('connected', (data) => {
      console.log('🔗 Server confirmed connection:', data);
    });

    this.socket.on('joined_room', (data) => {
      console.log(`🏠 Successfully joined room: ${data.room}`);
    });

    this.socket.on('left_room', (data) => {
      console.log(`👋 Left room for member: ${data.memberId}`);
    });
    
    // DEBUG: Stream event listeners (these should fire if events arrive)
    this.socket.on('stream_start', (data: any) => {
      console.log('🔔 [SocketService] stream_start received:', data);
    });
    
    this.socket.on('stream_chunk', (data: any) => {
      console.log('🔔 [SocketService] stream_chunk received (first 50 chars):', data.chunk?.substring(0, 50));
    });
    
    this.socket.on('stream_complete', (data: any) => {
      console.log('🔔 [SocketService] stream_complete received:', data);
    });
    
    // 🔔 Notification listener (NEW)
    this.socket.on('notification:new', (data: any) => {
      console.log('🔔 New notification received:', data);

      // Dispatch to React
      window.dispatchEvent(
        new CustomEvent('notification:new', {
          detail: data.notification,
        })
      );
    });

    // Catch-all listener to debug ALL socket events
    this.socket.onAny((eventName, ...args) => {
      console.log(`🔔 Socket.IO event: "${eventName}"`, args);
    });
  }

  /**
   * Disconnect from Socket.IO server and clean up
   */
  disconnect(): void {
    if (!this.socket) return;

    // Leave member room before disconnecting
    if (this.memberId) {
      this.socket.emit('leave_room', { memberId: this.memberId });
      console.log(`👋 Leaving room for member: ${this.memberId}`);
    }

    this.socket.disconnect();
    this.socket.removeAllListeners();
    this.socket = null;
    this.memberId = null;
    this.reconnectAttempts = 0;
    
    console.log('🔌 Socket disconnected and cleaned up');
  }

  /**
   * Get current socket instance
   */
  getSocket(): Socket<ServerToClientEvents, ClientToServerEvents> | null {
    return this.socket;
  }

  /**
   * Check if socket is currently connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current member ID
   */
  getMemberId(): string | null {
    return this.memberId;
  }

  /**
   * Get connection state information
   */
  getConnectionState() {
    return {
      connected: this.isConnected(),
      memberId: this.memberId,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Export singleton instance
export const socketService = new SocketService();
